/**
 * Colored icon badges for entity types in the Ads Manager.
 * Renders a rounded rect with a short label (C, AG, Ad).
 * Accepts publisherEntityType directly - no mapping needed at call sites.
 *
 * Note: Hardcoded hex colors are used because Meridian does not provide semantic tokens
 * for entity-type differentiation. These colors are consistent with the publisher-config-tab
 * branch styling and match the StatusBadge pill colors for each entity category.
 */
import React from 'react';
import type { PublisherEntityType } from 'src/store/generated/leela-api-generated';

const ICON_CONFIGS: Record<string, { bg: string; stroke: string; fill: string; label: string }> = {
  CAMPAIGN: { bg: '#ede9fe', stroke: '#a5b4fc', fill: '#6366f1', label: 'C' },
  AD_GROUP: { bg: '#dbeafe', stroke: '#93c5fd', fill: '#3b82f6', label: 'AG' },
  AD: { bg: '#ffedd5', stroke: '#fdba74', fill: '#f97316', label: 'Ad' },
  // Google carousel tree: AD -> CAROUSEL_CARD -> ASSET.
  CAROUSEL_CARD: { bg: '#ecfccb', stroke: '#bef264', fill: '#65a30d', label: 'Cd' },
  // ADSP tree: PRODUCT_SET (CCG root) -> CONCEPT -> CREATIVE -> ASSET.
  PRODUCT_SET: { bg: '#ccfbf1', stroke: '#5eead4', fill: '#0d9488', label: 'PS' },
  CONCEPT: { bg: '#fce7f3', stroke: '#f9a8d4', fill: '#db2777', label: 'Cn' },
  CREATIVE: { bg: '#fef3c7', stroke: '#fcd34d', fill: '#d97706', label: 'Cr' },
};

interface EntityIconProps {
  type: PublisherEntityType;
  size?: number;
}

export const EntityIcon: React.FC<EntityIconProps> = ({ type, size = 28 }) => {
  const config = ICON_CONFIGS[type];
  if (!config) return null;
  const { bg, stroke, fill, label } = config;

  return (
    <svg
      data-testid={`entity-icon-${type.toLowerCase()}`}
      height={size}
      viewBox="0 0 32 32"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill={bg} height="30" rx="6" stroke={stroke} strokeWidth="1.5" width="30" x="1" y="1" />
      <text
        dominantBaseline="central"
        fill={fill}
        fontFamily="system-ui, -apple-system, sans-serif"
        fontSize="11"
        fontWeight="700"
        textAnchor="middle"
        x="16"
        y="16"
      >
        {label}
      </text>
    </svg>
  );
};
